import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DatePickerField } from "./DatePickerField";
import { SessionSchedule, ScheduleMode, countSessionsInRange } from "@/lib/sessionSchedule";
import { cn } from "@/lib/utils";

const DAYS = [
  { v: 1, l: "Mon" }, { v: 2, l: "Tue" }, { v: 3, l: "Wed" }, { v: 4, l: "Thu" },
  { v: 5, l: "Fri" }, { v: 6, l: "Sat" }, { v: 0, l: "Sun" },
];

interface Props {
  startDate?: Date;
  value: SessionSchedule;
  onChange: (v: SessionSchedule) => void;
}

export function SessionScheduleField({ startDate, value, onChange }: Props) {
  const modes: { v: ScheduleMode; l: string }[] = [
    { v: "single", l: "Single session" },
    { v: "range", l: "Date range" },
    { v: "count", l: "Number of sessions" },
  ];
  const rangeCount = countSessionsInRange(startDate, value.endDate, value.weekdays);

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <Label>Sessions</Label>
      <div className="flex flex-wrap gap-2">
        {modes.map((m) => (
          <Button key={m.v} type="button" size="sm" variant={value.mode === m.v ? "default" : "outline"}
            onClick={() => onChange({ ...value, mode: m.v })}>
            {m.l}
          </Button>
        ))}
      </div>

      {value.mode === "range" && (
        <div className="space-y-3">
          <DatePickerField
            label="End date"
            value={value.endDate}
            onChange={(d) => onChange({ ...value, endDate: d })}
            defaultMonth={value.endDate ?? startDate}
            disabled={(d) => !!startDate && d < startDate}
          />
          <div className="space-y-1">
            <Label className="text-xs">Runs on</Label>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d) => {
                const on = value.weekdays.includes(d.v);
                return (
                  <button key={d.v} type="button"
                    onClick={() => onChange({ ...value, weekdays: on ? value.weekdays.filter((x) => x !== d.v) : [...value.weekdays, d.v] })}
                    className={cn("rounded border px-2 py-1 text-xs", on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>
                    {d.l}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-sm">
            {startDate && value.endDate
              ? <><strong>{rangeCount}</strong> sessions between the start and end date</>
              : <span className="text-muted-foreground">Pick the start date above and an end date to count sessions.</span>}
          </p>
        </div>
      )}

      {value.mode === "count" && (
        <div className="space-y-1">
          <Label className="text-xs">How many sessions</Label>
          <Input type="number" min={1} value={value.count ?? ""}
            onChange={(e) => onChange({ ...value, count: e.target.value ? parseInt(e.target.value, 10) : undefined })} />
        </div>
      )}

      {value.mode !== "single" && (
        <p className="text-xs text-muted-foreground">The cost per unit is charged once per session.</p>
      )}
    </div>
  );
}
